(ns ballot-scout-v2-frontend.util
  (:require
   [cljs-time.coerce :as coerce]
   [cljs-time.core :as cljs-time]
   [cljs-time.format :as timeformat]
   [clojure.string :as str]
   [goog.string.format])
  (:import
   (goog.date DateTime)
   (goog.date Date)
   (goog.date UtcDateTime)
   (goog.i18n DateTimeFormat)))

(defn within-six-months? [yyyymmdd-str]
  (if (string? yyyymmdd-str)
    (let [six-months-ago (cljs-time/minus (cljs-time/now) (cljs-time/months 6))
          given-date (timeformat/parse (timeformat/formatters :date) yyyymmdd-str)]
      (cljs-time/after? given-date six-months-ago))
    true))

(defn ->int [x]
  (if (string? x)
    (js/parseInt x 10)
    (int x)))

(defn in-the-past? [given-time]
  (let [sometime (coerce/from-string given-time)]
    (cljs-time/before? sometime (cljs-time/now))))

(defn after-day?
  "See if it's the next day or later than the day specified in `date`"
  [date]
  (some-> date
          coerce/from-string
          (cljs-time/floor cljs-time/day)
          (cljs-time/plus (cljs-time/days 1))
          (cljs-time/before? (cljs-time/now))))

;; A note on these datetime formats:
;; The following functions that call DateTimeFormat are using
;; the goog.i18n library. This library comes with some built-in i18n 
;; functions that detect the browser's preferred language(s) settings
;; hehind-the-scenes. 
;; When a preferred language is encountered, the library implictly sets 
;; some global JS vars which are then used by the DateTimeFormat
;; function to format the date for a particular language.
;; See more info: https://github.com/google/closure-library/blob/master/closure/goog/i18n/datetimeformat.js

;; Note also the i18n/set-datetime-patterns function for where we
;; directly set those JS globals, when triggered by a user language selection.

(defn date-str-to-date
  "Converts a YYYY-MM-DD string to a Date"
  [str]
  (when (not (nil? str))
    (.fromIsoString Date str)))

(defn render-date [d]
  (when (not (nil? d))
    (str (.format (DateTimeFormat. "EEEE, MMMM d, y") (DateTime. d)))))

(defn render-datetime [d]
  (when (not (nil? d))
    (str (.format (DateTimeFormat. "EEEE MMMM d YYYY, h:mm a") (DateTime. d)))))

(defn render-datetime-ampm [d]
  (when (not (nil? d))
    (str (.format (DateTimeFormat. "EE MMM dd YYYY, hh:mm a") (DateTime. d)))))

(defn render-weekday-month-date [d]
  (when (not (nil? d))
    (str (.format (DateTimeFormat. "EE MMM dd YYYY") (DateTime. d)))))

(defn format-utc
  "Formats the date `d`. Assumes that `d` already contains a date part in
  UTC, so will not perform any browser timezone conversion before formatting."
  [d]
  (let [utc-dt (UtcDateTime. d)]
    (.format (DateTimeFormat. "EEEE, MMMM d, y") utc-dt)))

(defn capitalize-each-word [phrase]
  (->> (str/split phrase #" ")
       (map str/capitalize)
       (str/join " ")))

(def backend-status-labels
  {:usps/returned "Returned to Sender" ;; needed to support dashboard
   :disposition/created "Not Scanned / No Data"
   :disposition/mailed "Released to USPS"
   :voter-bound/mailed "Mailed to Voter"
   :voter-bound/in-transit "Mailed to Voter"
   :voter-bound/out-for-delivery "Scheduled for Delivery to Voter"
   :voter-bound/delivered "Delivered to Voter"
   :voter-bound/returned "Returned to Sender"
   :office-bound/mailed "Mailed to Office"
   :office-bound/in-transit "Mailed to Office"
   :office-bound/out-for-delivery "Scheduled for Delivery to Office"
   :office-bound/delivered "Delivered to Office"
   :office-bound/returned "Returned to Sender"
   :disposition/received "Received by Office"
   :disposition/accepted "Accepted by Office"
   :disposition/flagged "Flagged by Office"})

(defn combine-returned-statuses [m]
  (let [sum (+ (:voter-bound/returned m) (:office-bound/returned m))]
    (if (< 0 sum)
      (-> m
          (assoc :usps/returned (+ (:voter-bound/returned m) (:office-bound/returned m)))
          (dissoc :voter-bound/returned)
          (dissoc :office-bound/returned))
      m)))

;; statuses that exist for old ballots in our system, but we should no longer be supporting
(def deprecated-statuses #{:voter-bound/mailed :office-bound/mailed})

(defn backend-status->label [status]
  (get backend-status-labels status))

(defn ns-keyword->str [status]
  (str/join "/" ((juxt namespace name) status)))

(defn remove-empty-values [m]
  (reduce-kv (fn [acc k v]
               (if (str/blank? v)
                 acc
                 (assoc acc k v)))
             {}
             m))

(defn trim-values [m]
  (reduce-kv (fn [acc k v]
               (assoc acc k (str/trim (str v))))
             {}
             m))

(defn remove-nils [m]
  (reduce-kv (fn [acc k v]
               (if (some? v)
                 (assoc acc k v)
                 acc))
             {}
             m))

(defn get-readable-filename-from-s3-link
  [filepath]
  (-> filepath
      (str/split #"\?")
      first
      (str/split #"/")
      last))

(defn round
  "Rounds n (String or Number) to two decimal places, returns a Number"
  [n]
  (if (nil? n)
    nil
    (let [n (js/parseFloat n)]
      (/ (Math/round (* 100 n)) 100))))
